import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';

import { SessionService } from './session.service';

/** Where the active partner is remembered across reloads (per-origin storage). */
const STORAGE_KEY = 'sisoma.active-partner';

/** The persisted entry: the chosen partner tagged with its owning user. */
interface StoredActivePartner {
  userId: string;
  partnerId: string;
}

/**
 * The partner currently in context for API calls — the seam between "which
 * partner is the user acting on" and the HTTP layer.
 *
 * `authInterceptor` reads {@link activePartnerId} and, when set, attaches it as
 * the `creatorSub` context header so the backend `CapabilitiesGuard` resolves
 * capabilities for the right partner (design doc §4 — the `creatorsub` header
 * precedent shared with `AccessTokenGuard`).
 *
 * `CapabilitiesService` is the writer, calling {@link setActivePartnerId} as the
 * user switches partner context. Kept separate from `CapabilitiesService` so the
 * interceptor depends only on this tiny piece of state, not the whole
 * capability-resolution service.
 *
 * The choice is **persisted** to `localStorage` and restored on construction:
 * the readable session cookie is short-lived and the app's landing route has no
 * partner id in its URL, so without this a reload would drop the user's
 * selection. Persistence is per-origin and best-effort (a blocked/erroring
 * store falls back to in-memory only).
 *
 * The persisted value is **tagged with the owning user id** so a second user on
 * a shared browser never inherits the first user's partner (which would leave
 * them stuck: `CapabilitiesService` only auto-selects when the active partner is
 * `null`, so an inherited id blocks the new user's own auto-select). On
 * construction the entry is restored only when it belongs to the current user;
 * when the session has lapsed and the user is not yet known, it is restored
 * optimistically and corrected by the capability guards after the silent refresh.
 * {@link clear} drops it on sign-out.
 */
@Injectable({ providedIn: 'root' })
export class ActivePartnerContext {
  private readonly storage = inject(DOCUMENT).defaultView?.localStorage;
  private readonly session = inject(SessionService);
  private readonly activePartner = signal<string | null>(this.read());

  /** The partner id sent as the `creatorSub` header, or `null` for none. */
  readonly activePartnerId = this.activePartner.asReadonly();

  /** Set (or clear, with `null`) the partner in context for API calls. */
  setActivePartnerId(id: string | null): void {
    this.activePartner.set(id);
    this.write(id);
  }

  /**
   * Reset the active partner and forget the persisted choice. Called from the
   * interceptor's sign-out path so a subsequent user on the same browser starts
   * from a clean slate rather than inheriting the previous selection.
   */
  clear(): void {
    this.setActivePartnerId(null);
  }

  private read(): string | null {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const entry: StoredActivePartner = JSON.parse(raw);
      if (typeof entry?.partnerId !== 'string' || typeof entry?.userId !== 'string') {
        // Malformed or a legacy plain-string entry — treat as absent; the user
        // re-selects or auto-selects once.
        return null;
      }
      const userId = this.session.userId();
      // Known user that owns the entry, or an as-yet-unknown user (lapsed token):
      // restore. A different known user must not inherit it.
      if (userId === null || userId === entry.userId) {
        return entry.partnerId;
      }
      return null;
    } catch {
      return null;
    }
  }

  private write(id: string | null): void {
    try {
      const userId = this.session.userId();
      // No id, or no known owner to tag it with → don't persist (in-memory only).
      if (id && userId) {
        const entry: StoredActivePartner = { userId, partnerId: id };
        this.storage?.setItem(STORAGE_KEY, JSON.stringify(entry));
      } else {
        this.storage?.removeItem(STORAGE_KEY);
      }
    } catch {
      // Storage unavailable (private mode / disabled) — in-memory state still
      // works for this session; persistence is best-effort.
    }
  }
}
